// ============================================================================
// Today + Upcoming — a compact, scannable list of what's next, from live data.
// ============================================================================
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Avatar, Badge, Card, CardHeader, EmptyState } from "../components";
import { IconClock, IconMapPin } from "../components/icons";
import { formatTime, formatWeekdayShort, isSameDay } from "../lib/date";
import { useDashboardCtx, sessionsOnDay } from "./DashboardDataContext";
import type { SessionVM } from "./useDashboardData";
import { SESSION_STYLE } from "./sessionStyle";
import { cn } from "../lib/cn";

export function UpcomingSessions() {
  const { t } = useTranslation();
  const data = useDashboardCtx();

  const { today, upcoming } = useMemo(() => {
    const now = new Date();
    const todayAll = sessionsOnDay(data, now).filter(
      (s) => s.status === "planned",
    );
    const nowMs = now.getTime();
    const up = data.sessions
      .filter(
        (s) =>
          s.status === "planned" &&
          new Date(s.startsAt).getTime() >= nowMs &&
          !isSameDay(new Date(s.startsAt), now),
      )
      .sort((a, b) => a.startsAt.localeCompare(b.startsAt))
      .slice(0, 8);
    return { today: todayAll, upcoming: up };
  }, [data]);

  const hasAny = today.length > 0 || upcoming.length > 0;

  return (
    <Card className="flex h-full flex-col">
      <CardHeader
        title={t("upcoming.title")}
        subtitle={t("upcoming.subtitle")}
        action={
          <Badge tone="volt" solid>
            {t("upcoming.todayBadge", { count: today.length })}
          </Badge>
        }
      />

      {!hasAny ? (
        <EmptyState
          title={t("upcoming.empty")}
          description={t("upcoming.emptyBlurb")}
          className="flex-1 !py-10"
        />
      ) : (
        <div className="-mr-1 flex-1 space-y-4 overflow-y-auto pr-1">
          {today.length > 0 && (
            <Group label={t("upcoming.today")}>
              {today.map((s) => (
                <Row key={s.id} session={s} showDay={false} />
              ))}
            </Group>
          )}
          {upcoming.length > 0 && (
            <Group label={t("upcoming.comingUp")}>
              {upcoming.map((s) => (
                <Row key={s.id} session={s} showDay />
              ))}
            </Group>
          )}
        </div>
      )}
    </Card>
  );
}

function Group({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-2 text-xs font-bold uppercase tracking-kicker text-body-faint">{label}</p>
      <ul className="space-y-2">{children}</ul>
    </div>
  );
}

function Row({ session, showDay }: { session: SessionVM; showDay: boolean }) {
  const { t } = useTranslation();
  const st = SESSION_STYLE[session.type];
  return (
    <li className="group flex items-center gap-3 rounded-md p-2 transition-colors hover:bg-surface-sunken">
      <div className="flex w-14 shrink-0 flex-col items-center">
        <span className={cn("rounded-pill px-2 py-0.5 text-xs font-extrabold tabular-nums text-white", st.grad)}>
          {formatTime(session.startsAt)}
        </span>
        {showDay && (
          <span className="mt-1 text-[0.62rem] font-bold text-body-faint">
            {formatWeekdayShort(session.startsAt)}
          </span>
        )}
      </div>
      <Avatar name={session.clientName} size="md" />
      <div className="min-w-0 flex-1">
        <p className="truncate font-display text-sm font-bold text-body">{session.clientName}</p>
        <p className="flex items-center gap-2 text-xs text-body-soft">
          <span className="inline-flex items-center gap-1">
            <IconClock width={12} height={12} />
            {t("dashboard:calendar.durationShort", { count: session.durationMin })}
          </span>
          {session.location && (
            <span className="inline-flex min-w-0 items-center gap-1 truncate">
              <IconMapPin width={12} height={12} />
              <span className="truncate">{session.location}</span>
            </span>
          )}
        </p>
      </div>
      <Badge tone={st.tone} className="shrink-0">{t(`dashboard:sessionType.${session.type}`)}</Badge>
    </li>
  );
}
